/**
 * /tracing-lightbox — the page. The screen of a tablet or laptop becomes a
 * light pad: a picture under the paper, at real size if asked, split into
 * screen-sized tiles when it is bigger than the screen, and a lock that a
 * resting palm cannot undo.
 *
 * Arithmetic (calibration, tiling, outline, dpi) lives in core.js and is
 * proved in node; painting lives in render.js. This file wires the page.
 *
 * Calibration is SHARED with /online-ruler (__toolSettings 'screen-calibration'
 * → pxPerMm + dpr): a person who calibrated there is calibrated here.
 */
import * as C from './core.js';
import * as R from './render.js';

const CFG = window.__TLB_CFG || {};
const UI = CFG.ui || {};
const SLUG = 'tracing-lightbox';
const $ = (id) => document.getElementById(id);
const NUM_LOCALE = CFG.locale === 'ar' ? 'ar-u-nu-latn' : (CFG.locale || 'en');

function t(key, rep) {
    const s = UI[key] != null ? String(UI[key]) : key;
    return rep ? s.replace(/:([a-z_]+)/g, (m, k) => (rep[k] != null ? String(rep[k]) : m)) : s;
}
function ts() { return window.__toolSettings || null; }
function load(k, fb) { try { const s = ts(); return s ? s.load(SLUG, k, fb) : fb; } catch (_) { return fb; } }
function save(k, v) { try { const s = ts(); if (s) s.save(SLUG, k, v); } catch (_) { /* storage off */ } }
function feature(f, p) { try { if (window.__aeFeature) window.__aeFeature(f, p || {}); } catch (_) { /* telemetry off */ } }
function stage(n, m) { try { if (window.__stage) window.__stage(n, m || {}); } catch (_) { /* telemetry off */ } }
function diag(p) { try { if (window.__aeDiagnostic) window.__aeDiagnostic(p); } catch (_) { /* telemetry off */ } }
function toast(msg, type) { try { if (window.__toast) window.__toast(msg, type || 'info'); } catch (_) { /* no toasts */ } }
function num(v, d) {
    try { return new Intl.NumberFormat(NUM_LOCALE, { maximumFractionDigits: d, minimumFractionDigits: 0 }).format(v); }
    catch (_) { return String(Math.round(v * Math.pow(10, d)) / Math.pow(10, d)); }
}
function parseNum(s) {
    const v = parseFloat(String(s == null ? '' : s).trim().replace(/\s/g, '').replace(',', '.'));
    return isFinite(v) ? v : NaN;
}
function len(mm, unit) {
    const u = unit || S.unit;
    return num(C.fromMm(mm, u), u === 'in' ? 2 : 1) + ' ' + t(u === 'in' ? 'unit_in' : 'unit_cm');
}
function isDark() { return document.documentElement.getAttribute('data-theme') !== 'light'; }

/* ── state ─────────────────────────────────────────────────────────────── */

function defaultUnit() {
    const l = (navigator.language || '').toUpperCase();
    return /-(US|LR|MM)$/.test(l) ? 'in' : 'cm';
}
const LOOK0 = { mode: 'photo', detail: 50, thick: 0.8, level: null, gray: false, invert: false, contrast: 0, brightness: 0, opacity: 100, grid: 0 };
function loadLook() {
    const l = Object.assign({}, LOOK0);
    for (const k of Object.keys(LOOK0)) {
        const v = load('look_' + k, undefined);
        if (v !== undefined && v !== null) l[k] = v;
    }
    if (!['photo', 'outline', 'threshold'].includes(l.mode)) l.mode = 'photo';
    for (const k of ['detail', 'thick', 'contrast', 'brightness', 'opacity', 'grid']) l[k] = Number(l[k]) || LOOK0[k];
    if (l.level != null) l.level = Number(l.level) || null;
    l.gray = !!l.gray; l.invert = !!l.invert;
    return l;
}

const S = {
    src: null,                 // {img, w, h, name, size, dpi}
    white: false,
    look: loadLook(),
    sizeMode: load('size_mode', 'free') === 'real' ? 'real' : 'free',
    unit: load('unit', defaultUnit()) === 'in' ? 'in' : 'cm',
    overlap: Math.max(0, Math.min(30, Number(load('overlap', 10)) || 0)),
    light: Math.max(30, Math.min(100, Number(load('light', 100)) || 100)),
    widthMm: null,
    angle: 0,
    mirror: false,
    free: { cx: 0.5, cy: 0.5, wf: null },   // centre as a share of the screen, picture width as a share of screen width
    pan: { x: 0, y: 0 },                    // mm, a real-size picture on one screen
    tile: 0,
    done: new Set(),
};

/* ── calibration (shared with /online-ruler) ──────────────────────────── */

let CAL = null;
function readCal() {
    const s = ts();
    let raw = null, dpr = null;
    try { if (s) { raw = s.load(C.CAL_TOOL, 'pxPerMm', null); dpr = s.load(C.CAL_TOOL, 'dpr', null); } } catch (_) { /* storage off */ }
    if (typeof raw === 'string') raw = parseFloat(raw);
    const now = window.devicePixelRatio || 1;
    const k = C.effectivePxPerMm(raw, dpr, now);
    CAL = k ? { k, raw, dpr, zoomed: !!(dpr && Math.abs(Number(dpr) - now) >= 0.01) } : null;
    return CAL;
}
function saveCal(k) {
    const s = ts();
    try { if (s) { s.save(C.CAL_TOOL, 'pxPerMm', k); s.save(C.CAL_TOOL, 'dpr', window.devicePixelRatio || 1); } } catch (_) { /* storage off */ }
    readCal();
}
function pxPerMm() { return CAL ? CAL.k : C.DEFAULT_PXMM; }
readCal();

/* ── elements ──────────────────────────────────────────────────────────── */

const drop = $('tlbDrop'), work = $('tlbWork'), prev = $('tlbPrev'), stageEl = $('tlbStage');
const lb = $('tlbLb'), lbc = $('tlbLbCanvas'), lbMap = $('tlbLbMap');
// No ancestor may hold the fixed full-screen layer (a transform or a clip on
// a wrapper turns `position:fixed` into «fixed inside that wrapper»).
if (lb && lb.parentNode !== document.body) document.body.appendChild(lb);

const hasFsApi = !!(document.documentElement.requestFullscreen || document.documentElement.webkitRequestFullscreen);
const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isAndroid = /Android/i.test(navigator.userAgent);

/* ── screen size (the light box is the whole screen) ──────────────────── */

let lbOpen = false, lastLb = null;
function isPortrait() {
    const o = screen.orientation && screen.orientation.type;
    if (o) return o.indexOf('portrait') === 0;
    return window.innerHeight >= window.innerWidth;
}
function screenCss() {
    if (lbOpen) return { w: lb.clientWidth || window.innerWidth, h: lb.clientHeight || window.innerHeight };
    const portrait = isPortrait();
    if (lastLb && lastLb.portrait === portrait) return { w: lastLb.w, h: lastLb.h };
    let a = window.innerWidth, b = window.innerHeight;
    if (hasFsApi && screen.width > 0 && screen.height > 0) { a = screen.width; b = screen.height; }
    const lo = Math.min(a, b), hi = Math.max(a, b);
    return portrait ? { w: lo, h: hi } : { w: hi, h: lo };
}

/* ── geometry of the picture ──────────────────────────────────────────── */

function picMm() { return { w: S.widthMm, h: S.widthMm * S.src.h / S.src.w }; }
function layoutFor(scr) {
    const k = pxPerMm(), pm = picMm();
    const box = C.rotatedBox(pm.w, pm.h, S.angle);
    const L = C.tileLayout({ contentW: box.w, contentH: box.h, tileW: scr.w / k, tileH: scr.h / k, overlap: S.overlap });
    return { L, k, pm, box, marks: C.registrationMarks(L) };
}
function picCenterMm(X) {
    const one = X.L.count === 1;
    return { x: X.L.offsetX + X.box.w / 2 + (one ? S.pan.x : 0), y: X.L.offsetY + X.box.h / 2 + (one ? S.pan.y : 0) };
}
function fitFree(scr, fill) {
    const tr = (fill ? C.fillTransform : C.fitTransform)(S.src.w, S.src.h, S.angle, scr.w, scr.h);
    setFree(tr, scr);
}
function freeToScreen(scr) {
    if (S.free.wf == null) fitFree(scr);
    return { x: S.free.cx * scr.w, y: S.free.cy * scr.h, s: S.free.wf * scr.w / S.src.w, a: S.angle };
}
function setFree(tr, scr) {
    S.free = { cx: tr.x / scr.w, cy: tr.y / scr.h, wf: tr.s * S.src.w / scr.w };
    S.angle = C.normAngle(tr.a);
}
function defaultWidthMm() {
    const d = S.src && C.meaningfulDpi(S.src.dpi);
    if (d) return C.widthForDpi(S.src.w, d);
    return C.widthForPaper(S.src.w, S.src.h, S.angle, S.unit === 'in' ? 'letter' : 'a4', 10) || 200;
}
/** CSS px the picture's width covers on the light box right now. */
function displayedCss() {
    if (!S.src) return 0;
    if (S.sizeMode === 'real') return S.widthMm * pxPerMm();
    const scr = screenCss();
    if (S.free.wf == null) fitFree(scr);
    return S.free.wf * scr.w;
}

/* ── the look: processed in a worker, newest request wins ─────────────── */

const WORK_MIN = 1600, WORK_CAP = 3200, DRAFT_MAX = 560;
let proc = { img: null, key: '' };
let worker = null, workerBroken = false, busy = false, pendingJob = null, current = null, jobId = 0, fullTimer = 0;

function needsProcessing() {
    const l = S.look;
    return l.mode !== 'photo' || !C.isIdentityLook(l);
}
/**
 * The working resolution follows how big the picture is SHOWN: a poster traced
 * at 80 cm over nine screens needs more pixels than a sketch fitted to one,
 * or its lines come out soft and thinner than asked. Photo mode is a tone
 * table and takes the whole source.
 */
function workSide() {
    const srcMax = Math.max(S.src.w, S.src.h);
    if (S.look.mode === 'photo') return srcMax;
    const shownW = displayedCss(), shownMax = shownW * Math.max(S.src.w, S.src.h) / S.src.w;
    return Math.min(srcMax, Math.max(WORK_MIN, Math.min(WORK_CAP, Math.round(shownMax))));
}
function buildJob(maxSide) {
    const side = maxSide;
    const sc = Math.min(1, side / Math.max(S.src.w, S.src.h));
    const w = Math.max(1, Math.round(S.src.w * sc)), h = Math.max(1, Math.round(S.src.h * sc));
    const l = S.look;
    const look = { mode: l.mode, detail: l.detail, level: l.level, gray: l.gray, invert: l.invert, contrast: l.contrast, brightness: l.brightness, dilate: 0 };
    if (l.mode === 'outline') look.dilate = C.dilationRadius(l.thick, pxPerMm(), displayedCss() / w);
    return { w, h, look, token: S.src.token, key: JSON.stringify([S.src.token, w, h, look]) };
}
function getWorker() {
    if (worker || workerBroken || !CFG.workerUrl) return worker;
    /* An engine without module workers (Firefox < 114, Safari < 15) would start the
     * worker as a classic script that dies on its first import. The look was drawn
     * on the main thread anyway, but under the shared «something went wrong» toast
     * (measured on an emulated engine, 08.10.2026). Asked first, it goes straight
     * to the main thread — the same core, the same pixels. */
    if (window.__moduleWorkerOk && !window.__moduleWorkerOk()) {
        workerBroken = true;
        diag({ issue: 'worker_skipped', msg: 'module_worker' });
        return null;
    }
    try {
        worker = new Worker(CFG.workerUrl, { type: 'module' });
        worker.onmessage = onWorkerMsg;
        worker.onerror = (e) => {
            // The look is redone on the main thread (below), so the failure is ours and
            // is not repeated on the page: uncancelled it would reach window.onerror.
            if (e && e.preventDefault) e.preventDefault();
            workerBroken = true;
            try { worker.terminate(); } catch (_) { /* gone */ }
            worker = null;
            diag({ issue: 'worker_failed', msg: String((e && e.message) || 'load').slice(0, 120) });
            if (current) { const j = current; current = null; busy = false; runJob(j); }
        };
    } catch (e) {
        workerBroken = true; worker = null;
        diag({ issue: 'worker_failed', msg: String((e && e.message) || e).slice(0, 120) });
    }
    return worker;
}
function schedule() {
    if (!S.src) return;
    clearTimeout(fullTimer);
    if (!needsProcessing()) {
        pendingJob = null;
        proc = { img: S.src.img, key: 'src' };
        requestDraw();
        return;
    }
    if (Math.max(S.src.w, S.src.h) > DRAFT_MAX * 1.3) request(buildJob(DRAFT_MAX));
    fullTimer = setTimeout(() => request(buildJob(workSide())), 260);
}
function request(job) {
    if (job.key === proc.key || (current && current.key === job.key)) return;
    if (busy) { pendingJob = job; return; }
    runJob(job);
}
function runJob(job) {
    if (!S.src) return;
    busy = true; current = job; job.id = ++jobId;
    $('tlbBusy').hidden = false;
    const cv = document.createElement('canvas');
    cv.width = job.w; cv.height = job.h;
    const cx = cv.getContext('2d', { willReadFrequently: true });
    cx.drawImage(S.src.img, 0, 0, job.w, job.h);
    let data;
    try { data = cx.getImageData(0, 0, job.w, job.h); }
    catch (e) { finish(job, null); diag({ issue: 'read_pixels_failed', msg: String(e && e.message).slice(0, 120) }); return; }
    const wk = getWorker();
    if (wk) {
        wk.postMessage({ id: job.id, w: job.w, h: job.h, buf: data.data.buffer, look: job.look }, [data.data.buffer]);
    } else {
        setTimeout(() => {
            const r = C.processLook(data.data, job.w, job.h, job.look);
            finish(job, r.rgba, r.level);
        }, 0);
    }
}
function onWorkerMsg(e) {
    const d = e.data || {};
    const job = current;
    if (!job || d.id !== job.id) return;
    if (d.error) {
        diag({ issue: 'process_failed', msg: d.error });
        workerBroken = true;
        try { worker.terminate(); } catch (_) { /* gone */ }
        worker = null; current = null; busy = false;
        runJob(job);
        return;
    }
    finish(job, new Uint8ClampedArray(d.buf), d.level);
}
function finish(job, rgba, level) {
    busy = false; current = null;
    if (rgba && S.src && job.token === S.src.token) {
        const cv = document.createElement('canvas');
        cv.width = job.w; cv.height = job.h;
        cv.getContext('2d').putImageData(new ImageData(rgba, job.w, job.h), 0, 0);
        proc = { img: cv, key: job.key };
        if (job.look.mode === 'threshold' && S.look.level == null && level != null) {
            $('tlbLevel').value = String(level);
            $('tlbLevelV').textContent = String(level);
        }
    }
    if (pendingJob) { const p = pendingJob; pendingJob = null; runJob(p); return; }
    $('tlbBusy').hidden = true;
    requestDraw();
}

/* ── drawing ───────────────────────────────────────────────────────────── */

let drawQueued = false;
function requestDraw() {
    if (drawQueued) return;
    drawQueued = true;
    requestAnimationFrame(() => { drawQueued = false; drawAll(); });
}
function drawAll() {
    if (!work.hidden) { drawPreview(); updateInfo(); }
    if (lbOpen) drawLightBox();
}
function picFor(cx, cy, s) {
    return { img: proc.img || S.src.img, srcW: S.src.w, srcH: S.src.h, cx, cy, s, a: S.angle, mirror: S.mirror, alpha: S.look.opacity / 100 };
}
function sizeCanvas(cv, W, H) {
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    const bw = Math.max(1, Math.round(W * dpr)), bh = Math.max(1, Math.round(H * dpr));
    if (cv.width !== bw) cv.width = bw;
    if (cv.height !== bh) cv.height = bh;
    cv.style.height = H + 'px';
    return dpr;
}

let PV = null;   // how the plan maps to the canvas, for hit-testing
function drawPreview() {
    if (!S.src) return;
    const scr = screenCss();
    const W = Math.max(200, stageEl.clientWidth);
    const maxH = Math.max(220, Math.min(640, window.innerHeight * 0.7));
    const pad = 10;
    let worldW, worldH, screens, pic, map;
    if (S.sizeMode === 'real') {
        const X = layoutFor(scr);
        worldW = X.L.gridW; worldH = X.L.gridH;
        const H = Math.max(180, Math.min(maxH, W * worldH / worldW));
        const p = Math.min((W - 2 * pad) / worldW, (H - 2 * pad) / worldH);
        const ox = (W - worldW * p) / 2, oy = (H - worldH * p) / 2;
        S.tile = Math.min(S.tile, X.L.count - 1);
        screens = X.L.tiles.map((tl) => ({ x: ox + tl.x * p, y: oy + tl.y * p, w: tl.w * p, h: tl.h * p,
            label: String(tl.index + 1), current: X.L.count > 1 && tl.index === S.tile, done: S.done.has(tl.index) }));
        const c = picCenterMm(X);
        pic = picFor(ox + c.x * p, oy + c.y * p, X.pm.w * p / S.src.w);
        map = { mode: 'real', p, ox, oy, X, W, H };
    } else {
        const m = 0.07 * Math.max(scr.w, scr.h);
        worldW = scr.w + 2 * m; worldH = scr.h + 2 * m;
        const H = Math.max(180, Math.min(maxH, W * worldH / worldW));
        const p = Math.min((W - 2 * pad) / worldW, (H - 2 * pad) / worldH);
        const ox = (W - scr.w * p) / 2, oy = (H - scr.h * p) / 2;
        const tr = freeToScreen(scr);
        screens = [{ x: ox, y: oy, w: scr.w * p, h: scr.h * p, label: '1', current: false }];
        pic = picFor(ox + tr.x * p, oy + tr.y * p, tr.s * p);
        map = { mode: 'free', p, ox, oy, scr, W, H };
    }
    const dpr = sizeCanvas(prev, map.W, map.H);
    prev.style.width = '100%';
    R.paintPlan(prev.getContext('2d'), { W: map.W, H: map.H, dpr, dark: isDark(), screens, pic, grid: S.look.grid });
    PV = map;
    stageEl.classList.toggle('tlb-tiles', S.sizeMode === 'real' && map.X.L.count > 1);
}

function updateInfo() {
    const scr = screenCss(), k = pxPerMm();
    const sw = scr.w / k, sh = scr.h / k;
    let info = t(CAL ? 'screen_info' : 'screen_uncal', { w: num(C.fromMm(sw, S.unit), 1), h: len(sh) });
    if (S.src) {
        let pw, ph;
        if (S.sizeMode === 'real') { pw = S.widthMm; ph = picMm().h; }
        else { pw = displayedCss() / k; ph = pw * S.src.h / S.src.w; }
        info += ' · ' + t('pic_info', { w: num(C.fromMm(pw, S.unit), S.unit === 'in' ? 2 : 1), h: len(ph) });
    }
    $('tlbScreenInfo').textContent = info;
    const pill = $('tlbTilesPill');
    let hint;
    if (S.sizeMode === 'real' && S.src) {
        const X = layoutFor(scr);
        pill.hidden = false;
        pill.textContent = '▦ ' + X.L.count;
        hint = X.L.count > 1 ? t('stage_tiles') : t('stage_one');
        const sum = $('tlbSummary');
        sum.textContent = '';
        const a = document.createElement('span');
        a.textContent = X.L.count > 1
            ? t('sum_tiles', { w: num(C.fromMm(X.box.w, S.unit), S.unit === 'in' ? 2 : 1), h: len(X.box.h), n: X.L.count, cols: X.L.cols, rows: X.L.rows })
            : t('sum_one', { w: num(C.fromMm(X.box.w, S.unit), S.unit === 'in' ? 2 : 1), h: len(X.box.h) });
        sum.appendChild(a);
        const adv = C.orientationAdvice(X.box.w, X.box.h, sw, sh, S.overlap);
        // a laptop cannot be turned; a tablet or a phone can
        const turnable = navigator.maxTouchPoints > 0 || (window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
        if (adv.better && turnable) {
            const b = document.createElement('span');
            b.className = 'tlb-muted';
            b.textContent = t('turn_advice', { better: adv.turned, now: adv.now });
            sum.appendChild(b);
        }
    } else {
        pill.hidden = true;
        hint = t('stage_free');
    }
    $('tlbStageHint').textContent = hint;
    $('tlbAngle').textContent = t('angle', { a: num(Math.round(S.angle * 10) / 10, 1) });
    $('tlbMirror').setAttribute('aria-pressed', String(S.mirror));
}

/* ── the light box ─────────────────────────────────────────────────────── */

let frozen = null;       // while locked: the layout / transform in absolute px, so nothing can shift
let lbCtx = null;
function lightScene(W, H) {
    const scene = { W, H, light: S.light / 100, pic: null, grid: S.look.grid, gridPx: 2, marks: null, strips: null, markR: 0 };
    if (S.white || !S.src) return scene;
    if (S.sizeMode === 'real') {
        const X = (frozen && frozen.X) || layoutFor({ w: W, h: H });
        S.tile = Math.max(0, Math.min(S.tile, X.L.count - 1));
        const tile = X.L.tiles[S.tile], k = X.k, c = picCenterMm(X);
        scene.pic = picFor((c.x - tile.x) * k, (c.y - tile.y) * k, X.pm.w * k / S.src.w);
        scene.marks = C.marksInTile(X.L, X.marks, S.tile, 20).map((m) => ({ x: m.x * k, y: m.y * k }));
        scene.strips = C.overlapStrips(X.L, S.tile).map((s) => ({ side: s.side, x: s.x * k, y: s.y * k, w: s.w * k, h: s.h * k }));
        // the cross stays inside its overlap band where it can (a cross cut by the
        // screen edge still shows its centre, which is what gets lined up)
        scene.markR = Math.max(10, k * Math.max(3, Math.min(6, X.L.overlap * 0.45)));
        scene.X = X;
    } else {
        const tr = (frozen && frozen.tr) || freeToScreen({ w: W, h: H });
        scene.pic = picFor(tr.x, tr.y, tr.s);
    }
    return scene;
}
function drawLightBox() {
    const W = lb.clientWidth || window.innerWidth, H = lb.clientHeight || window.innerHeight;
    const dpr = sizeCanvas(lbc, W, H);
    lbc.style.width = W + 'px';
    if (!lbCtx) lbCtx = lbc.getContext('2d');
    const scene = lightScene(W, H);
    scene.dpr = dpr;
    R.paintLight(lbCtx, scene);
    const X = scene.X;
    const tiled = !!(X && X.L.count > 1);
    // while locked nothing may sit on the paper that cannot be pressed anyway
    $('tlbLbBot').hidden = !tiled || locked;
    $('tlbLbTip').hidden = !tiled || locked;
    const tag = $('tlbLbTag'), corner = $('tlbLbCorner');
    if (tiled) {
        const lab = C.tileLabel(X.L, S.tile);
        const txt = t('lb_tile', { n: lab.n, total: lab.total, row: lab.row, col: lab.col });
        tag.hidden = false; tag.textContent = txt;
        corner.hidden = false; corner.textContent = lab.n + '/' + lab.total;
        $('tlbLbPrev').disabled = S.tile <= 0;
        $('tlbLbNext').disabled = S.tile >= X.L.count - 1;
        drawMiniMap(X);
    } else {
        tag.hidden = true; corner.hidden = true;
    }
}
let MM = null;
function drawMiniMap(X) {
    const maxW = Math.min(180, window.innerWidth - 180), maxH = 110;
    const p = Math.min((maxW - 12) / X.L.gridW, (maxH - 12) / X.L.gridH);
    const W = Math.max(40, X.L.gridW * p + 12), H = Math.max(30, X.L.gridH * p + 12);
    const dpr = sizeCanvas(lbMap, W, H);
    lbMap.style.width = W + 'px';
    const ox = 6, oy = 6;
    const screens = X.L.tiles.map((tl) => ({ x: ox + tl.x * p, y: oy + tl.y * p, w: tl.w * p, h: tl.h * p,
        label: String(tl.index + 1), current: tl.index === S.tile, done: S.done.has(tl.index) }));
    const c = picCenterMm(X);
    const pic = picFor(ox + c.x * p, oy + c.y * p, X.pm.w * p / S.src.w);
    R.paintPlan(lbMap.getContext('2d'), { W, H, dpr, dark: true, screens, pic, grid: 0, numbers: X.L.count <= 30 });
    MM = { p, ox, oy, X };
}

function goTile(i, markDone) {
    const scr = screenCss();
    const X = (frozen && frozen.X) || layoutFor(scr);
    const n = Math.max(0, Math.min(X.L.count - 1, i));
    if (n === S.tile) return;
    if (markDone) S.done.add(S.tile);
    S.tile = n;
    feature('tile_nav', { n: n + 1, of: X.L.count });
    saveImgState();
    requestDraw();
}

// The shared primitive (public/js/wake-lock.js) keeps the screen on and repairs
// the hold after the page was hidden itself (WakeLockPrimitiveTest).
function wakeOn() {
    try { if (window.__wakeLock && window.__wakeLock.hold) window.__wakeLock.hold('tracing-lightbox'); } catch (_) { /* optional */ }
}
function wakeOff() {
    try { if (window.__wakeLock && window.__wakeLock.releaseHold) window.__wakeLock.releaseHold('tracing-lightbox'); } catch (_) { /* optional */ }
}

let histPushed = false, ignorePop = false, lbOpenedAt = 0;
function openLightBox(white) {
    if (lbOpen) return;
    S.white = !!white;
    lb.hidden = false;
    lbOpen = true;
    lbOpenedAt = performance.now();
    document.documentElement.classList.add('tlb-lb-open');
    if (hasFsApi && window.__safeFullscreen) {
        window.__safeFullscreen(lb, {}).then(() => requestDraw());
    }
    wakeOn();
    try { history.pushState({ tlb: 1 }, ''); histPushed = true; } catch (_) { histPushed = false; }
    $('tlbLbLight').value = String(S.light);
    drawLightBox();
    try { $('tlbLbLock').focus({ preventScroll: true }); } catch (_) { /* old engines */ }
    const X = !S.white && S.src && S.sizeMode === 'real' ? layoutFor(screenCss()) : null;
    stage(S.white ? 'white_light' : 'lightbox_open', { size: S.sizeMode, tiles: X ? X.L.count : 1 });
    feature('lightbox_open', { white: S.white ? 1 : 0, size: S.sizeMode, look: S.look.mode, tiles: X ? X.L.count : 1, cal: CAL ? 1 : 0, fs: hasFsApi ? 1 : 0 });
    checkZoomWarn();
}
function closeLightBox(fromPop) {
    if (!lbOpen) return;
    if (locked) return;
    lastLb = { w: lb.clientWidth, h: lb.clientHeight, portrait: isPortrait() };
    lbOpen = false;
    lb.hidden = true;
    $('tlbLockInfo').hidden = true;
    document.documentElement.classList.remove('tlb-lb-open');
    wakeOff();
    try {
        const fe = document.fullscreenElement || document.webkitFullscreenElement;
        if (fe && window.__safeExitFullscreen) window.__safeExitFullscreen();   // a prefixed exit returns no Promise (TIMBRICA-FRONTEND-NQ)
    } catch (_) { /* nothing to exit */ }
    if (histPushed && !fromPop) { histPushed = false; ignorePop = true; try { history.back(); } catch (_) { ignorePop = false; } }
    histPushed = false;
    S.white = false;
    saveImgState();
    requestDraw();
    try { $('tlbGo').focus({ preventScroll: true }); } catch (_) { /* old engines */ }
}
window.addEventListener('popstate', () => {
    if (ignorePop) { ignorePop = false; return; }
    if (!lbOpen) return;
    if (locked) {
        // the back gesture must not take the page away from under the paper
        try { history.pushState({ tlb: 1 }, ''); histPushed = true; } catch (_) { /* no history API */ }
        flashLocked();
        return;
    }
    histPushed = false;
    closeLightBox(true);
});
function onFsChange() {
    const fe = document.fullscreenElement || document.webkitFullscreenElement;
    if (!fe && lbOpen && !locked && hasFsApi && performance.now() - lbOpenedAt > 400) {
        // Esc / the browser's own exit: the light box goes with it
        closeLightBox(false);
    }
    requestDraw();
}
document.addEventListener('fullscreenchange', onFsChange);
document.addEventListener('webkitfullscreenchange', onFsChange);

function checkZoomWarn() {
    const vv = window.visualViewport;
    const w = $('tlbLbWarn');
    if (vv && vv.scale > 1.02 && lbOpen) { w.hidden = false; w.textContent = t('lb_zoomed'); }
    else if (w.dataset.kind !== 'resized') w.hidden = true;
}

/* ── the lock ─────────────────────────────────────────────────────────── */

let locked = false, lockedAt = 0, blocked = 0, completed = false, completeTimer = 0, lockSize = null;
const LOCK_EVENTS = ['pointerdown', 'pointermove', 'pointerup', 'pointercancel', 'touchstart', 'touchmove', 'touchend', 'touchcancel',
    'mousedown', 'mousemove', 'mouseup', 'click', 'dblclick', 'auxclick', 'contextmenu', 'wheel', 'gesturestart', 'gesturechange',
    'gestureend', 'selectstart', 'dragstart', 'keydown', 'keyup', 'keypress'];
function guard(e) {
    if (!locked) return;
    const tg = e.target;
    const own = tg && tg.closest && tg.closest('[data-tlb-unlock]');
    // the hold button needs no click, so its touches are cancelled too (no
    // long-press menu, no magnifier); pointer events still reach it
    if (own && own.id === 'tlbUnlock' && (e.type === 'touchstart' || e.type === 'touchmove')) {
        if (e.cancelable) e.preventDefault();
        return;
    }
    if (own && e.type !== 'contextmenu' && e.type !== 'selectstart' && e.type !== 'dragstart'
        && e.type !== 'wheel' && e.type.indexOf('key') !== 0 && e.type.indexOf('gesture') !== 0) return;
    if (e.cancelable) e.preventDefault();
    e.stopImmediatePropagation();
    if (e.type === 'pointerdown' || (e.type === 'touchstart' && !window.PointerEvent)) { blocked++; flashLocked(); }
}
for (const ev of LOCK_EVENTS) window.addEventListener(ev, guard, { capture: true, passive: false });

let flashTimer = 0, taps = [], lastHint = 0;
function flashLocked() {
    // every blocked touch lights the lock up for a moment; the words come only
    // when someone is clearly tapping at the screen (3 touches in 2 s), not
    // for a palm that rests and shifts while tracing
    const u = $('tlbUnlock'), h = $('tlbUnhint');
    const now = performance.now();
    u.classList.add('tlb-flash');
    taps = taps.filter((x) => now - x < 2000);
    taps.push(now);
    if (taps.length >= 3 && now - lastHint > 8000) {
        lastHint = now;
        h.hidden = false;
        h.textContent = t('lb_blocked');
    }
    clearTimeout(flashTimer);
    flashTimer = setTimeout(() => { u.classList.remove('tlb-flash'); h.hidden = true; }, 1600);
}

function askLock() {
    if (locked) return;
    if (load('lock_info_seen', false)) { doLock(); return; }
    $('tlbLkIos').hidden = !isIOS;
    $('tlbLkAndroid').hidden = !isAndroid;
    $('tlbLkWake').hidden = !!window.__wakeLock;
    $('tlbLockInfo').hidden = false;
    try { $('tlbLkGo').focus({ preventScroll: true }); } catch (_) { /* old engines */ }
}
function doLock() {
    $('tlbLockInfo').hidden = true;
    const W = lb.clientWidth, H = lb.clientHeight;
    frozen = null;
    if (!S.white && S.src) {
        if (S.sizeMode === 'real') frozen = { X: layoutFor({ w: W, h: H }) };
        else frozen = { tr: freeToScreen({ w: W, h: H }) };
    }
    lockSize = { w: W, h: H };
    locked = true;
    lockedAt = performance.now();
    blocked = 0;
    lb.classList.add('tlb-locked');
    document.documentElement.classList.add('tlb-lockdoc');
    $('tlbLbTop').hidden = true;
    $('tlbUnlock').hidden = false;
    $('tlbLbWarn').hidden = true;
    try { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); } catch (_) { /* nothing focused */ }
    const fe = document.fullscreenElement || document.webkitFullscreenElement;
    if (!fe && hasFsApi && window.__safeFullscreen) window.__safeFullscreen(lb, {});
    wakeOn();
    try {
        if (screen.orientation && screen.orientation.lock) screen.orientation.lock(screen.orientation.type).catch(() => { /* not in fullscreen / not supported */ });
    } catch (_) { /* no orientation API */ }
    // tell once where the way out is
    const h = $('tlbUnhint');
    h.hidden = false; h.textContent = t('lb_hold');
    clearTimeout(flashTimer);
    flashTimer = setTimeout(() => { h.hidden = true; }, 3500);
    stage('locked', { size: S.sizeMode });
    feature('lock', { white: S.white ? 1 : 0, size: S.sizeMode, wake: window.__wakeLock ? window.__wakeLock.capability() : 'none', fs: fe || hasFsApi ? 1 : 0 });
    clearTimeout(completeTimer);
    completeTimer = setTimeout(markComplete, 20000);
    drawLightBox();
}
function markComplete() {
    if (completed || !locked) return;
    completed = true;
    const X = !S.white && S.src && S.sizeMode === 'real' && frozen && frozen.X;
    try {
        if (window.__aeToolComplete) window.__aeToolComplete({
            mode: S.white ? 'white' : 'image', size: S.sizeMode, look: S.look.mode,
            tiles: X ? X.L.count : 1, cal: CAL ? 1 : 0, grid: S.look.grid, mirror: S.mirror ? 1 : 0,
        });
    } catch (_) { /* telemetry off */ }
}
function unlock() {
    if (!locked) return;
    locked = false;
    clearTimeout(completeTimer);
    const sec = Math.round((performance.now() - lockedAt) / 1000);
    feature('unlock', { sec, blocked });
    frozen = null;
    lb.classList.remove('tlb-locked');
    document.documentElement.classList.remove('tlb-lockdoc');
    $('tlbLbTop').hidden = false;
    $('tlbUnlock').hidden = true;
    $('tlbUnhint').hidden = true;
    $('tlbConfirmWrap').hidden = true;
    const w = $('tlbLbWarn'); w.hidden = true; delete w.dataset.kind;
    try { if (screen.orientation && screen.orientation.unlock) screen.orientation.unlock(); } catch (_) { /* no orientation API */ }
    resetRing();
    requestDraw();
    try { $('tlbLbLock').focus({ preventScroll: true }); } catch (_) { /* old engines */ }
}

/* hold the corner for 2 s, then tap the ✓ that appears in the middle */
const HOLD_MS = 2000, CONFIRM_MS = 6000;
let hold = null, confirmTimer = 0, confirmAt = 0;
const RING_LEN = 157.1;
function resetRing() {
    $('tlbRing').setAttribute('stroke-dashoffset', String(RING_LEN));
    $('tlbUnlock').classList.remove('tlb-holding');
}
function holdFrame() {
    if (!hold) return;
    const f = Math.min(1, (performance.now() - hold.t0) / HOLD_MS);
    $('tlbRing').setAttribute('stroke-dashoffset', String(RING_LEN * (1 - f)));
    if (f >= 1) { hold = null; resetRing(); showConfirm(); return; }
    hold.raf = requestAnimationFrame(holdFrame);
}
function cancelHold() {
    if (!hold) return;
    cancelAnimationFrame(hold.raf);
    hold = null;
    resetRing();
}
const unlockBtn = $('tlbUnlock');
unlockBtn.addEventListener('pointerdown', (e) => {
    if (!locked) return;
    e.preventDefault();
    try { unlockBtn.setPointerCapture(e.pointerId); } catch (_) { /* synthetic pointer */ }
    hold = { id: e.pointerId, x: e.clientX, y: e.clientY, t0: performance.now(), raf: 0 };
    unlockBtn.classList.add('tlb-holding');
    $('tlbUnhint').hidden = false;
    $('tlbUnhint').textContent = t('lb_hold');
    hold.raf = requestAnimationFrame(holdFrame);
});
unlockBtn.addEventListener('pointermove', (e) => {
    if (hold && e.pointerId === hold.id && Math.hypot(e.clientX - hold.x, e.clientY - hold.y) > 30) cancelHold();
});
for (const ev of ['pointerup', 'pointercancel', 'lostpointercapture']) {
    unlockBtn.addEventListener(ev, (e) => { if (hold && e.pointerId === hold.id) cancelHold(); });
}
function showConfirm() {
    const w = $('tlbConfirmWrap');
    w.hidden = false;
    confirmAt = performance.now();
    clearTimeout(confirmTimer);
    confirmTimer = setTimeout(() => { w.hidden = true; }, CONFIRM_MS);
    $('tlbUnhint').hidden = true;
}
$('tlbConfirm').addEventListener('click', () => {
    // a click needs its press ON this button, which did not exist a moment
    // ago — a palm that was already resting here cannot produce one
    if (!locked || performance.now() - confirmAt < 120) return;
    clearTimeout(confirmTimer);
    unlock();
});

window.addEventListener('resize', () => {
    readCal();
    if (lbOpen) {
        if (locked && lockSize && (Math.abs(lb.clientWidth - lockSize.w) > 4 || Math.abs(lb.clientHeight - lockSize.h) > 4)) {
            const w = $('tlbLbWarn');
            w.hidden = false; w.dataset.kind = 'resized'; w.textContent = t('lb_resized');
        } else if (locked) {
            const w = $('tlbLbWarn');
            if (w.dataset.kind === 'resized') { w.hidden = true; delete w.dataset.kind; }
        }
        checkZoomWarn();
    }
    requestDraw();
});
if (window.visualViewport) window.visualViewport.addEventListener('resize', checkZoomWarn);

/* light box buttons */
$('tlbLbExit').addEventListener('click', () => closeLightBox(false));
$('tlbLbLock').addEventListener('click', askLock);
$('tlbLkGo').addEventListener('click', () => {
    if ($('tlbLkNever').checked) save('lock_info_seen', true);
    doLock();
});
$('tlbLkCancel').addEventListener('click', () => { $('tlbLockInfo').hidden = true; });
$('tlbLbLight').addEventListener('input', (e) => {
    S.light = Math.max(30, Math.min(100, Number(e.target.value) || 100));
    save('light', S.light);
    requestDraw();
});
$('tlbLbPrev').addEventListener('click', () => goTile(S.tile - 1, false));
$('tlbLbNext').addEventListener('click', () => goTile(S.tile + 1, true));
lbMap.addEventListener('click', (e) => {
    if (!MM) return;
    const r = lbMap.getBoundingClientRect();
    const x = (e.clientX - r.left - MM.ox) / MM.p, y = (e.clientY - r.top - MM.oy) / MM.p;
    const i = C.tileAt(MM.X.L, x, y);
    if (i >= 0) goTile(i, false);
});
document.addEventListener('keydown', (e) => {
    if (!lbOpen || locked) return;
    if (!$('tlbLockInfo').hidden) { if (e.key === 'Escape') { $('tlbLockInfo').hidden = true; e.preventDefault(); } return; }
    if (e.key === 'Escape') { closeLightBox(false); e.preventDefault(); }
    else if (e.key === 'ArrowRight' || e.key === 'PageDown') { goTile(S.tile + 1, true); e.preventDefault(); }
    else if (e.key === 'ArrowLeft' || e.key === 'PageUp') { goTile(S.tile - 1, false); e.preventDefault(); }
});

/* ── gestures: the plan on the page and the light box itself ───────────── */

function attachGestures(el, toWorld) {
    const pts = new Map();
    let g = null;
    function begin() {
        if (!S.src || S.white) { g = null; return; }
        const list = [...pts.values()];
        const scr = el === lbc ? { w: lb.clientWidth, h: lb.clientHeight } : screenCss();
        const w = list.map((p) => toWorld(p.x, p.y));
        if (S.sizeMode === 'free') g = { kind: 'free', scr, t0: freeToScreen(scr), w0: w, moved: false };
        else {
            const X = layoutFor(scr);
            g = { kind: X.L.count === 1 ? 'pan' : 'tiles', X, pan0: { ...S.pan }, w0: w, moved: false };
        }
        g.start = list.map((p) => ({ x: p.x, y: p.y }));
    }
    el.addEventListener('pointerdown', (e) => {
        if (locked) return;
        try { el.setPointerCapture(e.pointerId); } catch (_) { /* synthetic */ }
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY, t: performance.now() });
        begin();
        e.preventDefault();
    });
    el.addEventListener('pointermove', (e) => {
        if (!pts.has(e.pointerId) || !g) return;
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY, t: pts.get(e.pointerId).t });
        const list = [...pts.values()];
        const w = list.map((p) => toWorld(p.x, p.y));
        if (!g.moved && list.some((p, i) => g.start[i] && Math.hypot(p.x - g.start[i].x, p.y - g.start[i].y) > 5)) g.moved = true;
        if (!g.moved) return;
        if (g.kind === 'free') {
            let tr;
            if (w.length >= 2 && g.w0.length >= 2) tr = C.pinchUpdate(g.t0, g.w0[0], g.w0[1], w[0], w[1]);
            else tr = { ...g.t0, x: g.t0.x + (w[0].x - g.w0[0].x), y: g.t0.y + (w[0].y - g.w0[0].y) };
            tr.a = C.snapAngle(tr.a, 2);
            setFree(tr, g.scr);
        } else if (g.kind === 'pan') {
            // both surfaces hand back grid millimetres here
            S.pan = { x: g.pan0.x + (w[0].x - g.w0[0].x), y: g.pan0.y + (w[0].y - g.w0[0].y) };
        }
        requestDraw();
    });
    function end(e) {
        if (!pts.has(e.pointerId)) return;
        const p = pts.get(e.pointerId);
        pts.delete(e.pointerId);
        // a tap on the PLAN picks a screen; a tap on the light box itself does nothing
        if (el === prev && g && !g.moved && g.kind === 'tiles' && e.type === 'pointerup' && performance.now() - p.t < 600) {
            const wp = toWorld(e.clientX, e.clientY);
            onTileTap(wp);
        }
        if (g && g.moved) { if (g.kind === 'free') { schedule(); feature('gesture', { k: 'free' }); } saveImgState(); }
        if (pts.size) begin(); else g = null;
    }
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
    el.addEventListener('dblclick', () => {
        if (locked || !S.src || S.sizeMode !== 'free') return;
        const scr = el === lbc ? { w: lb.clientWidth, h: lb.clientHeight } : screenCss();
        fitFree(scr);
        schedule(); requestDraw(); saveImgState();
    });
    el.addEventListener('wheel', (e) => {
        if (locked || !S.src || S.sizeMode !== 'free') return;
        // on the page, plain scrolling scrolls the page; Ctrl (and a trackpad pinch) zooms
        if (el === prev && !e.ctrlKey && !e.metaKey) return;
        e.preventDefault();
        const scr = el === lbc ? { w: lb.clientWidth, h: lb.clientHeight } : screenCss();
        const w = toWorld(e.clientX, e.clientY);
        const f = Math.exp(-Math.max(-60, Math.min(60, e.deltaY)) * 0.004);
        setFree(C.zoomAt(freeToScreen(scr), w.x, w.y, f), scr);
        requestDraw();
        clearTimeout(el.__tlbWheel);
        el.__tlbWheel = setTimeout(() => { schedule(); saveImgState(); }, 250);
    }, { passive: false });
}
function onTileTap(wp) {
    if (!PV || PV.mode !== 'real') return;
    const i = C.tileAt(PV.X.L, wp.x, wp.y);
    if (i >= 0) { S.tile = i; feature('tile_pick', { n: i + 1, of: PV.X.L.count }); saveImgState(); requestDraw(); }
}
// The plan: page px → screen px (free) or grid mm (real)
attachGestures(prev, (cx, cy) => {
    const r = prev.getBoundingClientRect();
    const x = cx - r.left, y = cy - r.top;
    if (!PV) return { x: 0, y: 0 };
    return { x: (x - PV.ox) / PV.p, y: (y - PV.oy) / PV.p };
});
// The light box: screen px are the world in free mode; mm on the current tile in real mode
attachGestures(lbc, (cx, cy) => {
    if (S.sizeMode === 'real') {
        const X = layoutFor({ w: lb.clientWidth, h: lb.clientHeight });
        const tile = X.L.tiles[Math.min(S.tile, X.L.count - 1)];
        return { x: tile.x + cx / X.k, y: tile.y + cy / X.k };
    }
    return { x: cx, y: cy };
});

/* ── the page controls ─────────────────────────────────────────────────── */

function pressed(sel, attr, val) {
    document.querySelectorAll(sel).forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute(attr) === val)));
}
function syncSizeUi() {
    pressed('[data-size]', 'data-size', S.sizeMode);
    pressed('[data-unit]', 'data-unit', S.unit);
    $('tlbFree').hidden = S.sizeMode !== 'free';
    $('tlbReal').hidden = S.sizeMode !== 'real';
    const cl = $('tlbCalLine');
    if (CAL) {
        const k = CAL.k;
        $('tlbCalText').textContent = (S.unit === 'in' ? t('cal_ok_in', { px: num(k * 25.4, 1) }) : t('cal_ok', { px: num(k * 10, 1) }))
            + (CAL.zoomed ? ' ' + t('cal_zoom') : '');
        $('tlbCalBtn').textContent = t('cal_again');
        cl.classList.remove('tlb-warn');
    } else {
        $('tlbCalText').textContent = t('cal_none');
        $('tlbCalBtn').textContent = t('cal_btn');
        cl.classList.add('tlb-warn');
    }
    if (S.src && S.sizeMode === 'real') {
        const wIn = $('tlbWidth'), hIn = $('tlbHeight');
        const dig = S.unit === 'in' ? 2 : 1;
        const fmt = (mm) => String(Math.round(C.fromMm(mm, S.unit) * Math.pow(10, dig)) / Math.pow(10, dig));
        if (document.activeElement !== wIn) wIn.value = fmt(S.widthMm);
        if (document.activeElement !== hIn) hIn.value = fmt(picMm().h);
        const d = C.meaningfulDpi(S.src.dpi);
        $('tlbDpiLine').hidden = !d;
        if (d) {
            const wmm = C.widthForDpi(S.src.w, d);
            $('tlbDpiText').textContent = t('dpi_file', { dpi: d, w: num(C.fromMm(wmm, S.unit), dig), h: len(wmm * S.src.h / S.src.w) });
        }
    }
    const ov = $('tlbOverlap');
    ov.value = String(S.overlap);
    $('tlbOverlapV').textContent = S.unit === 'in' ? num(S.overlap / 25.4, 2) + ' ' + t('unit_in') : num(S.overlap, 0) + ' ' + t('unit_mm');
}
function syncLookUi() {
    const l = S.look;
    pressed('[data-mode]', 'data-mode', l.mode);
    document.querySelectorAll('[data-for-mode]').forEach((el) => { el.hidden = el.getAttribute('data-for-mode') !== l.mode; });
    document.querySelectorAll('[data-hide-mode]').forEach((el) => { el.hidden = el.getAttribute('data-hide-mode') === l.mode; });
    const set = (id, v, txt) => { $(id).value = String(v); $(id + 'V').textContent = txt; };
    set('tlbDetail', l.detail, String(l.detail));
    set('tlbThick', l.thick, num(l.thick, 1) + ' ' + t('unit_mm'));
    set('tlbContrast', l.contrast, (l.contrast > 0 ? '+' : '') + l.contrast);
    set('tlbBright', l.brightness, (l.brightness > 0 ? '+' : '') + l.brightness);
    set('tlbOpacity', l.opacity, l.opacity + '%');
    if (l.level != null) set('tlbLevel', l.level, String(l.level));
    $('tlbAuto').setAttribute('aria-pressed', String(l.level == null));
    $('tlbGray').setAttribute('aria-pressed', String(l.gray));
    $('tlbInvert').setAttribute('aria-pressed', String(l.invert));
    $('tlbGrid').value = String(l.grid);
}
function setLook(k, v, reprocess) {
    S.look[k] = v;
    save('look_' + k, v);
    syncLookUi();
    if (reprocess) schedule();
    requestDraw();
}

document.querySelectorAll('[data-mode]').forEach((b) => b.addEventListener('click', () => {
    setLook('mode', b.getAttribute('data-mode'), true);
    feature('look_mode', { m: S.look.mode });
}));
$('tlbDetail').addEventListener('input', (e) => setLook('detail', Number(e.target.value), true));
$('tlbThick').addEventListener('input', (e) => setLook('thick', Number(e.target.value), true));
$('tlbLevel').addEventListener('input', (e) => setLook('level', Number(e.target.value), true));
$('tlbAuto').addEventListener('click', () => setLook('level', null, true));
$('tlbContrast').addEventListener('input', (e) => setLook('contrast', Number(e.target.value), true));
$('tlbBright').addEventListener('input', (e) => setLook('brightness', Number(e.target.value), true));
$('tlbOpacity').addEventListener('input', (e) => setLook('opacity', Number(e.target.value), false));
$('tlbGray').addEventListener('click', () => setLook('gray', !S.look.gray, true));
$('tlbInvert').addEventListener('click', () => setLook('invert', !S.look.invert, true));
$('tlbGrid').addEventListener('change', (e) => { setLook('grid', Number(e.target.value) || 0, false); feature('grid', { n: S.look.grid }); });
$('tlbResetLook').addEventListener('click', () => {
    for (const k of Object.keys(LOOK0)) { S.look[k] = LOOK0[k]; save('look_' + k, LOOK0[k]); }
    syncLookUi(); schedule(); requestDraw();
});

function afterSizeChange() {
    syncSizeUi();
    if (S.look.mode === 'outline') schedule();
    saveImgState();
    requestDraw();
}
document.querySelectorAll('[data-size]').forEach((b) => b.addEventListener('click', () => {
    const m = b.getAttribute('data-size');
    if (m === S.sizeMode) return;
    S.sizeMode = m;
    save('size_mode', m);
    if (m === 'real' && S.src && !(S.widthMm > 0)) S.widthMm = defaultWidthMm();
    if (m === 'real' && !CAL) { openCalib(); toast(t('cal_needed'), 'info'); }
    feature('size_mode', { m, cal: CAL ? 1 : 0 });
    afterSizeChange();
}));
document.querySelectorAll('[data-unit]').forEach((b) => b.addEventListener('click', () => {
    S.unit = b.getAttribute('data-unit');
    save('unit', S.unit);
    afterSizeChange();
}));
function onSizeInput(which) {
    const el = $(which === 'w' ? 'tlbWidth' : 'tlbHeight');
    const v = C.toMm(parseNum(el.value), S.unit);
    if (!(v >= 10 && v <= 20000)) return false;
    S.widthMm = which === 'w' ? v : v * S.src.w / S.src.h;
    afterSizeChange();
    return true;
}
for (const [id, w] of [['tlbWidth', 'w'], ['tlbHeight', 'h']]) {
    $(id).addEventListener('input', () => onSizeInput(w));
    $(id).addEventListener('change', () => { if (!onSizeInput(w)) toast(t('bad_size'), 'warning'); syncSizeUi(); feature('size_typed', { mm: Math.round(S.widthMm) }); });
    $(id).addEventListener('keydown', (e) => { if (e.key === 'Enter') e.target.blur(); });
}
document.querySelectorAll('[data-paper]').forEach((b) => b.addEventListener('click', () => {
    if (!S.src) return;
    const key = b.getAttribute('data-paper');
    const w = C.widthForPaper(S.src.w, S.src.h, S.angle, key, 10);
    if (!w) return;
    S.widthMm = w;
    S.pan = { x: 0, y: 0 };
    toast(t('paper_set', { paper: b.textContent.trim() }), 'success');
    feature('paper_fit', { p: key });
    afterSizeChange();
}));
$('tlbDpiUse').addEventListener('click', () => {
    const d = S.src && C.meaningfulDpi(S.src.dpi);
    if (!d) return;
    S.widthMm = C.widthForDpi(S.src.w, d);
    feature('dpi_size', { dpi: d });
    afterSizeChange();
});
$('tlbOverlap').addEventListener('input', (e) => {
    S.overlap = Math.max(0, Math.min(30, Number(e.target.value) || 0));
    save('overlap', S.overlap);
    S.done.clear();
    afterSizeChange();
});
$('tlbCalBtn').addEventListener('click', () => openCalib());

function turn(d) {
    if (!S.src) return;
    if (S.sizeMode === 'free') {
        // a quarter turn of a fitted landscape picture would overflow a landscape screen
        S.angle = C.normAngle(S.angle + d);
        fitFree(screenCss());
    } else {
        S.angle = C.normAngle(S.angle + d);
        S.done.clear();
    }
    feature('rotate', { a: Math.round(S.angle) });
    afterSizeChange();
}
$('tlbRotL').addEventListener('click', () => turn(-90));
$('tlbRotR').addEventListener('click', () => turn(90));
$('tlbMirror').addEventListener('click', () => { S.mirror = !S.mirror; feature('mirror', { on: S.mirror ? 1 : 0 }); afterSizeChange(); });
$('tlbFit').addEventListener('click', () => { if (S.src) { fitFree(screenCss()); afterSizeChange(); } });
$('tlbFill').addEventListener('click', () => { if (S.src) { fitFree(screenCss(), true); afterSizeChange(); } });
function zoomBy(f) {
    if (!S.src) return;
    const scr = screenCss(), tr = freeToScreen(scr);
    setFree(C.zoomAt(tr, tr.x, tr.y, f), scr);
    afterSizeChange();
}
$('tlbZoomIn').addEventListener('click', () => zoomBy(1.15));
$('tlbZoomOut').addEventListener('click', () => zoomBy(1 / 1.15));

$('tlbGo').addEventListener('click', () => openLightBox(false));
$('tlbWhite').addEventListener('click', () => openLightBox(true));

/* ── calibration panel ─────────────────────────────────────────────────── */

let calMethod = 'card';
function openCalib() {
    const c = $('tlbCalib');
    c.hidden = false;
    syncCalib();
    try { c.scrollIntoView({ block: 'nearest' }); } catch (_) { /* old engines */ }
    stage('calibrate_open');
}
function syncCalib() {
    const k = pxPerMm();
    pressed('[data-cal]', 'data-cal', calMethod);
    $('tlbCalCard').hidden = calMethod !== 'card';
    $('tlbCalRuler').hidden = calMethod !== 'ruler';
    const box = $('tlbCalib').clientWidth - 30;
    const shape = $('tlbCardShape');
    const landscape = C.CARD_W_MM * k <= box;
    shape.style.width = (landscape ? C.CARD_W_MM : C.CARD_H_MM) * k + 'px';
    shape.style.height = (landscape ? C.CARD_H_MM : C.CARD_W_MM) * k + 'px';
    $('tlbCalSlider').value = String(Math.round(k * 100));
    $('tlbCalState').textContent = CAL ? t('cal_saved') : '';
    drawBar();
    drawCheck();
}
function setCal(k, method) {
    saveCal(C.clampPxPerMm(k));
    stage('calibrated', { m: method });
    feature('calibrate', { m: method, k: Math.round(pxPerMm() * 100) / 100 });
    syncCalib(); syncSizeUi();
    if (S.look.mode === 'outline') schedule();
    requestDraw();
}
document.querySelectorAll('[data-cal]').forEach((b) => b.addEventListener('click', () => { calMethod = b.getAttribute('data-cal'); syncCalib(); }));
$('tlbCalSlider').addEventListener('input', (e) => setCal(Number(e.target.value) / 100, 'card'));
$('tlbCalMinus').addEventListener('click', () => setCal(pxPerMm() - 0.01, 'card'));
$('tlbCalPlus').addEventListener('click', () => setCal(pxPerMm() + 0.01, 'card'));
$('tlbBarApply').addEventListener('click', () => {
    const mm = parseNum($('tlbBarLen').value);
    const k = C.calibFromBar(BAR_PX, mm);
    if (!k) { toast(t('cal_bad_len'), 'warning'); return; }
    setCal(k, 'ruler');
});
$('tlbBarLen').addEventListener('keydown', (e) => { if (e.key === 'Enter') $('tlbBarApply').click(); });
$('tlbCalDone').addEventListener('click', () => { $('tlbCalib').hidden = true; syncSizeUi(); requestDraw(); });

let BAR_PX = 300;
function drawBar() {
    const cv = $('tlbBar');
    if ($('tlbCalRuler').hidden) return;
    const W = Math.max(120, cv.clientWidth), H = 44;
    const dpr = sizeCanvas(cv, W, H);
    cv.style.width = '100%';
    BAR_PX = Math.floor(W - 16);
    const x = cv.getContext('2d');
    x.setTransform(dpr, 0, 0, dpr, 0, 0);
    x.clearRect(0, 0, W, H);
    const x0 = 8;
    for (let i = 0; i < BAR_PX; i += 12) {
        x.fillStyle = (i / 12) % 2 ? '#f59e0b' : '#1e293b';
        x.fillRect(x0 + i, 8, Math.min(12, BAR_PX - i), 22);
    }
    x.fillStyle = '#e11d74';
    x.fillRect(x0 - 1, 2, 2, 34);
    x.fillRect(x0 + BAR_PX - 1, 2, 2, 34);
}
function drawCheck() {
    const cv = $('tlbCheck');
    const W = Math.max(120, cv.clientWidth), H = 46;
    const dpr = sizeCanvas(cv, W, H);
    cv.style.width = '100%';
    const k = pxPerMm();
    const inch = S.unit === 'in';
    const opts = inch ? [101.6, 50.8, 25.4] : [100, 50, 20];
    const L = opts.find((mm) => mm * k <= W - 16) || opts[opts.length - 1];
    $('tlbCheckLbl').textContent = t('cal_check', { len: len(L) });
    const x = cv.getContext('2d');
    x.setTransform(dpr, 0, 0, dpr, 0, 0);
    x.clearRect(0, 0, W, H);
    const ink = isDark() ? '#e2e8f0' : '#0f172a';
    x.strokeStyle = ink; x.fillStyle = ink; x.lineWidth = 1;
    x.font = '12px system-ui, sans-serif';
    x.beginPath();
    const x0 = 8;
    if (inch) {
        for (let s = 0; s <= L / 25.4 * 8 + 1e-6; s++) {
            const px = x0 + s * 25.4 / 8 * k, big = s % 8 === 0;
            x.moveTo(px + 0.5, 4); x.lineTo(px + 0.5, big ? 26 : (s % 4 === 0 ? 20 : 13));
            if (big) x.fillText(String(s / 8), px + 3, 40);
        }
    } else {
        for (let m = 0; m <= L; m++) {
            const px = x0 + m * k, big = m % 10 === 0;
            x.moveTo(px + 0.5, 4); x.lineTo(px + 0.5, big ? 26 : (m % 5 === 0 ? 20 : 13));
            if (big) x.fillText(String(m / 10), px + 3, 40);
        }
    }
    x.stroke();
}

/* ── loading a picture ─────────────────────────────────────────────────── */

let heicLoading = null;
function ensureHeic() {
    if (window.HeicDecode) return Promise.resolve(window.HeicDecode);
    if (heicLoading) return heicLoading;
    heicLoading = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        const timer = setTimeout(() => fail(new Error('heic_timeout')), 20000);
        function fail(e) { clearTimeout(timer); heicLoading = null; try { s.remove(); } catch (_) { /* gone */ } reject(e); }
        s.src = CFG.heicUrl;
        s.onload = () => { clearTimeout(timer); if (window.HeicDecode) resolve(window.HeicDecode); else fail(new Error('heic_missing')); };
        s.onerror = () => fail(new Error('heic_script'));
        document.head.appendChild(s);
    });
    return heicLoading;
}
function isHeic(f) {
    return /\.(heic|heif)$/i.test(f.name || '') || /image\/hei[cf]/i.test(f.type || '');
}
async function rasterizeSvg(file) {
    const text = await file.text();
    let w = 0, h = 0;
    const vb = /viewBox\s*=\s*["']\s*([-\d.eE]+)[\s,]+([-\d.eE]+)[\s,]+([-\d.eE]+)[\s,]+([-\d.eE]+)/.exec(text);
    if (vb) { w = parseFloat(vb[3]); h = parseFloat(vb[4]); }
    const wa = /<svg[^>]*\bwidth\s*=\s*["']([\d.]+)/i.exec(text), ha = /<svg[^>]*\bheight\s*=\s*["']([\d.]+)/i.exec(text);
    if (wa && ha) { w = parseFloat(wa[1]); h = parseFloat(ha[1]); }
    if (!(w > 0) || !(h > 0)) { w = 1000; h = 1000; }
    const k = 2400 / Math.max(w, h);
    const W = Math.max(1, Math.round(w * k)), H = Math.max(1, Math.round(h * k));
    const sized = text.replace(/<svg\b([^>]*)>/i, (m, attrs) =>
        '<svg' + attrs.replace(/\s(width|height)\s*=\s*["'][^"']*["']/gi, '') + ' width="' + W + '" height="' + H + '">');
    const url = URL.createObjectURL(new Blob([sized], { type: 'image/svg+xml' }));
    try {
        const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
        const c = document.createElement('canvas');
        c.width = W; c.height = H;
        c.getContext('2d').drawImage(img, 0, 0, W, H);
        return c;
    } finally { try { URL.revokeObjectURL(url); } catch (_) { /* gone */ } }
}
async function decode(file) {
    let f = file;
    if (isHeic(f)) {
        const H = await ensureHeic();
        const blob = await H.toBlob(f, 'image/jpeg', 0.92, window.__ae || null);
        f = new File([Array.isArray(blob) ? blob[0] : blob], (f.name || 'photo').replace(/\.(heic|heif)$/i, '') + '.jpg', { type: 'image/jpeg' });
    }
    let img = null;
    if (/svg/i.test(f.type || '') || /\.svg$/i.test(f.name || '')) img = await rasterizeSvg(f);
    if (!img && window.createImageBitmap) { try { img = await createImageBitmap(f); } catch (_) { img = null; } }
    if (!img) {
        const url = URL.createObjectURL(f);
        img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
    }
    let w = img.width || img.naturalWidth, h = img.height || img.naturalHeight;
    if (!(w > 0) || !(h > 0)) throw new Error('empty image');
    // a 50-megapixel panorama is not sharper under paper, only heavier
    const MAX = 4096;
    if (Math.max(w, h) > MAX) {
        const sc = MAX / Math.max(w, h);
        const c = document.createElement('canvas');
        c.width = Math.round(w * sc); c.height = Math.round(h * sc);
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        try { if (img.close) img.close(); } catch (_) { /* not a bitmap */ }
        img = c; w = c.width; h = c.height;
    }
    return { img, w, h, file: f };
}

let tokenSeq = 0, restoring = null;
async function loadFile(file) {
    if (!file) return;
    let dpi = null;
    try { dpi = C.readDpi(new Uint8Array(await file.slice(0, 262144).arrayBuffer())); } catch (_) { dpi = null; }
    let d;
    try { d = await decode(file); }
    catch (e) {
        toast(t(isHeic(file) ? 'image_error_heic' : 'image_error'), 'error');
        diag({ issue: 'image_decode_failed', type: String(file.type || '').slice(0, 20), kb: Math.round((file.size || 0) / 1024), msg: String((e && e.message) || e).slice(0, 100) });
        return;
    }
    try { if (S.src && S.src.img && S.src.img.close) S.src.img.close(); } catch (_) { /* not a bitmap */ }
    S.src = { img: d.img, w: d.w, h: d.h, name: file.name || 'picture', size: file.size || 0, dpi, token: ++tokenSeq };
    proc = { img: null, key: '' };
    pendingJob = null;
    const st = restoring && restoring.name === S.src.name && restoring.size === S.src.size ? restoring : null;
    restoring = null;
    if (st) {
        S.widthMm = st.widthMm > 0 ? st.widthMm : null;
        S.angle = Number(st.angle) || 0;
        S.mirror = !!st.mirror;
        S.free = st.free && st.free.wf > 0 ? st.free : { cx: 0.5, cy: 0.5, wf: null };
        S.pan = st.pan || { x: 0, y: 0 };
        S.tile = Number(st.tile) || 0;
        S.done = new Set(Array.isArray(st.done) ? st.done : []);
    } else {
        S.widthMm = null; S.angle = 0; S.mirror = false;
        S.free = { cx: 0.5, cy: 0.5, wf: null }; S.pan = { x: 0, y: 0 }; S.tile = 0; S.done = new Set();
    }
    if (!(S.widthMm > 0)) S.widthMm = defaultWidthMm();
    work.hidden = false;
    $('tlbResume').hidden = true;
    syncSizeUi(); syncLookUi();
    schedule();
    requestDraw();
    stage('image_loaded', { w: d.w, h: d.h });
    feature('image_loaded', { w: d.w, h: d.h, kb: Math.round((file.size || 0) / 1024), dpi: C.meaningfulDpi(dpi) || 0,
        kind: String(file.type || '').replace('image/', '').slice(0, 12), resumed: st ? 1 : 0 });
    if (!st) { saveImgState(); rememberFile(file); }
}

(function bindUploader() {
    const onFile = (f) => { if (Array.isArray(f)) f = f[0]; if (f) loadFile(f); };
    if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(drop, onFile);
    else drop.addEventListener('tool-uploader:file', (e) => { const d = e.detail; onFile(d && (d.file || (d.files && d.files[0]))); });
    drop.addEventListener('tool-uploader:clear', () => {
        try { if (S.src && S.src.img && S.src.img.close) S.src.img.close(); } catch (_) { /* not a bitmap */ }
        S.src = null; proc = { img: null, key: '' };
        work.hidden = true;
        forgetFile();
    });
})();
function inject(file, source) {
    if (window.ToolUploader && window.ToolUploader.inject) {
        try { window.ToolUploader.inject('tlbDrop', file, source); return; } catch (_) { /* fall through */ }
    }
    loadFile(file);
}

$('tlbPaste').addEventListener('click', async () => {
    if (!navigator.clipboard || !navigator.clipboard.read) { toast(t('paste_denied'), 'warning'); return; }
    try {
        const items = await navigator.clipboard.read();
        for (const it of items) {
            const type = (it.types || []).find((x) => x.indexOf('image/') === 0);
            if (!type) continue;
            const blob = await it.getType(type);
            const ext = (type.split('/')[1] || 'png').replace('jpeg', 'jpg').replace(/\+.*/, '');
            inject(new File([blob], 'clipboard.' + ext, { type }), 'clipboard_button');
            feature('paste', { type });
            return;
        }
        toast(t('paste_empty'), 'warning');
    } catch (_) {
        toast(t('paste_denied'), 'warning');
    }
});

/* ── continue where you stopped (the picture in IndexedDB, state in settings) ── */

const DB = 'tlb-last', STORE = 'kv';
function idb() {
    return new Promise((res, rej) => {
        if (!window.indexedDB) { rej(new Error('no idb')); return; }
        const r = indexedDB.open(DB, 1);
        r.onupgradeneeded = () => { try { r.result.createObjectStore(STORE); } catch (_) { /* exists */ } };
        r.onsuccess = () => res(r.result);
        r.onerror = () => rej(r.error);
        r.onblocked = () => rej(new Error('blocked'));
    });
}
async function idbDo(mode, fn) {
    const db = await idb();
    try {
        return await new Promise((res, rej) => {
            const tx = db.transaction(STORE, mode);
            const req = fn(tx.objectStore(STORE));
            tx.oncomplete = () => res(req && req.result);
            tx.onerror = () => rej(tx.error);
            tx.onabort = () => rej(tx.error);
        });
    } finally { try { db.close(); } catch (_) { /* closed */ } }
}
function rememberFile(file) {
    if (!file || file.size > 40 * 1024 * 1024) return;
    idbDo('readwrite', (s) => s.put({ blob: file, name: file.name || 'picture', type: file.type || '', size: file.size || 0, at: Date.now() }, 'last')).catch(() => { /* private mode */ });
}
function forgetFile() {
    idbDo('readwrite', (s) => s.delete('last')).catch(() => { /* private mode */ });
    save('img_state', null);
}
let saveTimer = 0;
function saveImgState() {
    if (!S.src) return;
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
        save('img_state', { name: S.src.name, size: S.src.size, widthMm: S.widthMm, angle: S.angle, mirror: S.mirror,
            free: S.free, pan: S.pan, tile: S.tile, done: [...S.done] });
    }, 300);
}
(async function offerResume() {
    let rec = null;
    try { rec = await idbDo('readonly', (s) => s.get('last')); } catch (_) { rec = null; }
    if (!rec || !rec.blob || S.src) return;
    const st = load('img_state', null);
    $('tlbResumeName').textContent = rec.name;
    if (st && st.name === rec.name && Array.isArray(st.done) && st.done.length) {
        const n = $('tlbResumeNote');
        n.hidden = false;
        n.textContent = t('resume_tiles', { n: st.done.length });
    }
    $('tlbResume').hidden = false;
    $('tlbResumeOpen').addEventListener('click', () => {
        restoring = st && st.name === rec.name ? st : null;
        const f = new File([rec.blob], rec.name, { type: rec.type || rec.blob.type || '' });
        inject(f, 'resume');
        feature('resume', {});
    }, { once: true });
    $('tlbResumeForget').addEventListener('click', () => { forgetFile(); $('tlbResume').hidden = true; }, { once: true });
})();

/* other tabs (the Online Ruler) may calibrate while this page is open */
window.addEventListener('storage', (e) => {
    if (e.key && e.key.indexOf('ts_' + C.CAL_TOOL + '_') === 0) { readCal(); syncSizeUi(); requestDraw(); }
});
new MutationObserver(() => requestDraw()).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

syncSizeUi();
syncLookUi();
window.__tlbDebug = {
    state: () => ({
        sizeMode: S.sizeMode, widthMm: S.widthMm, angle: S.angle, mirror: S.mirror, free: { ...S.free }, pan: { ...S.pan },
        tile: S.tile, done: [...S.done], locked, lbOpen, look: { ...S.look }, cal: CAL ? CAL.k : null,
        tiles: S.src && S.sizeMode === 'real' ? layoutFor(screenCss()).L.count : null, proc: proc.key ? 1 : 0, busy,
    }),
};
